import { getWidgetConfig } from '../runtime/widgetConfig'

export type EditorCategoryTreeItem = {
  id: string
  _id: number
  title: string
  parentId: number | null
  parent?: {
    id: string
    _id: number
    title: string
  } | null
  thumbnail?: string | null
  thumbnailWebp?: string | null
  editorType?: string | null
  editorConfig?: any
  showInEditor?: boolean | null
  position?: number | null
  childIds?: number[]
  descendantIds?: number[]
}

export type EditorCategoryTreeResponse = {
  count: number
  roots: EditorCategoryTreeItem[]
  items: EditorCategoryTreeItem[]
}

export type EditorCatalogProductSummary = {
  id: string
  _id: number
  title: string
  titleShort?: string | null
  description?: string | null
  shortDescription?: string | null
  price?: number | null
  basePrice?: number | null
  shippingInfo?: string | null
  shippingDuration?: string | null
  thumbnail?: string | null
  thumbnailWebp?: string | null
  position?: number | null
  editorOverrideType?: string | null
  editorOverrideConfig?: any
  /** Serverseitig gemergte Editor-Konfiguration je Kategorie-ID. */
  editorConfigByCategory?: Record<string, any> | null
  categoryIds?: number[]
  categories?: Array<any>
  configurator?: {
    product: {
      id: string
      title: string
      basePrice: number
      hasIndividualSizes: boolean
      minSizeX?: number | null
      minSizeY?: number | null
      maxSizeX?: number | null
      maxSizeY?: number | null
    }
    groups: any[]
  } | null
}

export type EditorCatalogBootstrapResponse = {
  tree: EditorCategoryTreeResponse
  products: Record<string, EditorCatalogProductSummary>
  productsByCategory: Record<string, number[]>
}

const inflight = new Map<string, Promise<EditorCatalogBootstrapResponse>>()
const cache = new Map<string, { expiresAt: number; value: EditorCatalogBootstrapResponse }>()
const BOOTSTRAP_TIMEOUT_MS = 30_000

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit, timeoutMs: number) {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), timeoutMs)
  try {
    return await fetch(input, {
      ...init,
      signal: ctrl.signal,
    })
  } finally {
    clearTimeout(timer)
  }
}

function resolveEditorBaseUrl(editorBaseUrl?: string | null): string {
  return String(editorBaseUrl || getWidgetConfig().editorBaseUrl || '').trim().replace(/\/+$/, '')
}

export async function fetchEditorCatalogBootstrap(input?: {
  editorBaseUrl?: string | null
  cacheTtlMs?: number
}): Promise<EditorCatalogBootstrapResponse> {
  const editorBaseUrl = resolveEditorBaseUrl(input?.editorBaseUrl)
  if (!editorBaseUrl) throw new Error('editor_base_url_missing')

  const cacheTtlMs = Math.max(0, Number(input?.cacheTtlMs ?? 5 * 60 * 1000) || 0)
  const url = `${editorBaseUrl}/categories/bootstrap`
  const now = Date.now()
  const cached = cache.get(url)
  if (cached && cached.expiresAt > now) return cached.value

  const running = inflight.get(url)
  if (running) return running

  const req = (async () => {
    const res = await fetchWithTimeout(url, {
      method: 'GET',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    }, BOOTSTRAP_TIMEOUT_MS)
    if (!res.ok) throw new Error(`editor_catalog_bootstrap_http_${res.status}`)
    const json = await res.json()
    const payload: EditorCatalogBootstrapResponse = {
      tree: {
        count: Number(json?.tree?.count ?? 0) || 0,
        roots: Array.isArray(json?.tree?.roots) ? json.tree.roots : [],
        items: Array.isArray(json?.tree?.items) ? json.tree.items : [],
      },
      products: json?.products && typeof json.products === 'object' ? json.products : {},
      productsByCategory: json?.productsByCategory && typeof json.productsByCategory === 'object' ? json.productsByCategory : {},
    }
    // Only cache responses that look structurally valid, so a transient bad/empty
    // response (e.g. missing tree.items) isn't pinned for the full TTL. The payload
    // is still returned to the caller for this request.
    const looksValid = Array.isArray(json?.tree?.items)
    if (looksValid) {
      cache.set(url, { value: payload, expiresAt: Date.now() + cacheTtlMs })
    }
    return payload
  })()

  inflight.set(url, req)
  try {
    return await req
  } finally {
    inflight.delete(url)
  }
}

export function clearEditorCatalogBootstrapCache(editorBaseUrl?: string | null) {
  const editorBase = resolveEditorBaseUrl(editorBaseUrl)
  if (!editorBase) return
  const url = `${editorBase}/categories/bootstrap`
  inflight.delete(url)
  cache.delete(url)
}
